import React from "react";
import { ChevronRight } from "lucide-react";
import type { AdminDashboardData } from "@/services/api/dashboard";
import { fallbackIcons, getToneStyle, iconMap } from "./dashboardUi";
import { SectionHeader } from "./SectionHeader";

type AgentPerformanceProps = {
  performance: AdminDashboardData["agentPerformance"];
};

export function AgentPerformance({ performance }: AgentPerformanceProps) {
  const chartMaximumValue = performance.chartMaximumValue || 100;
  const availableDateRanges = performance.availableDateRanges || [];
  const dailyPerformance = performance.dailyPerformance || [];
  const defaultDateRange =
    availableDateRanges.find((option) => option.isSelected) ||
    availableDateRanges[0];
  const [selectedDateRangeId, setSelectedDateRangeId] = React.useState(
    defaultDateRange?.dateRangeId || "",
  );
  const [isDateRangeOpen, setIsDateRangeOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const selectedDateRange =
    availableDateRanges.find(
      (option) => option.dateRangeId === selectedDateRangeId,
    ) || defaultDateRange;

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsDateRangeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader title={performance.sectionTitle}>
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isDateRangeOpen}
            className="inline-flex h-8 min-w-[116px] items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            onClick={() => setIsDateRangeOpen((isOpen) => !isOpen)}
          >
            {selectedDateRange?.dateRangeLabel || performance.dateRangeLabel}
            <ChevronRight
              className={`rotate-90 transition-transform ${
                isDateRangeOpen ? "-scale-x-100" : ""
              }`}
              size={15}
            />
          </button>

          {isDateRangeOpen && (
            <div
              role="listbox"
              aria-label="Agent performance date range"
              className="absolute right-0 z-20 mt-2 w-40 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
            >
              {availableDateRanges.map((option) => {
                const isSelected =
                  option.dateRangeId === selectedDateRange?.dateRangeId;

                return (
                  <button
                    key={option.dateRangeId}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold ${
                      isSelected
                        ? "bg-blue-50 text-blue-700"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                    onClick={() => {
                      setSelectedDateRangeId(option.dateRangeId);
                      setIsDateRangeOpen(false);
                    }}
                  >
                    {option.dateRangeLabel}
                    {isSelected && (
                      <span className="h-1.5 w-1.5 rounded-full bg-blue-600" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </SectionHeader>

      <div className="grid h-[170px] grid-cols-[32px_minmax(0,1fr)] gap-3">
        <div className="flex h-[135px] flex-col justify-between text-xs font-semibold text-slate-500">
          {performance.yAxisScaleLabels.map((scaleLabel) => (
            <span key={scaleLabel}>{scaleLabel}</span>
          ))}
        </div>
        <div
          className="grid h-[142px] items-end gap-3 border-b border-slate-300 bg-[linear-gradient(to_top,#e2e8f0_1px,transparent_1px)] bg-[length:100%_35px] pt-2"
          aria-label="Agent performance for the selected date range"
          style={{
            gridTemplateColumns: `repeat(${Math.max(
              dailyPerformance.length,
              1,
            )}, minmax(0, 1fr))`,
          }}
        >
          {dailyPerformance.map((item) => (
            <div
              key={item.dateLabel}
              className="flex h-full min-w-0 flex-col items-center justify-end"
            >
              <span className="mb-1 text-xs font-bold text-slate-700">
                {item.completedCalls}
              </span>
              <div
                className="w-6 rounded-t-md bg-blue-500 shadow-sm shadow-blue-200"
                style={{
                  height: `${Math.min(
                    (item.completedCalls / chartMaximumValue) * 100,
                    100,
                  )}%`,
                }}
              />
              <span className="mt-2 whitespace-nowrap text-xs font-medium text-slate-500">
                {item.dateLabel}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
        {performance.summaryCards.map((item) => {
          const Icon = iconMap[item.iconName] || fallbackIcons.users;
          const tone = getToneStyle(item.colorTone);

          return (
            <div
              key={item.summaryId}
              className={`flex min-h-[88px] items-center gap-3 rounded-lg border p-4 ${tone.soft} ${tone.border}`}
            >
              {/* <Icon size={30} className={tone.text} /> */}
              <div>
                <p className="m-0 text-xs font-bold text-slate-800">
                  {item.summaryTitle}
                </p>
                <p className="mt-1 text-2xl font-bold leading-none text-slate-950">
                  {item.summaryValue}
                </p>
                <p className="mt-2 text-xs font-semibold text-green-600">
                  {item.summaryDetail}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
